import { useState, useMemo, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { shiftService } from '../pages/personas/turnos/services/shiftService';

const EMPTY_TURNOS = [];

const formatTimeForDisplay = (isoString) => {
  if (!isoString) return '';
  try {
    const date = new Date(isoString);
    return date.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit', hour12: false });
  } catch (e) {
    return '';
  }
};

export const useTurnos = (factoryId = null) => {
  const [turnoSeleccionado, setTurnoSeleccionado] = useState(null);

  // Cargar turnos usando TanStack Query
  const {
    data: turnosData,
    isLoading: loading,
    error: queryError,
    refetch: cargarTurnos
  } = useQuery({
    queryKey: factoryId ? ['shifts', 'factory', factoryId] : ['shifts'],
    queryFn: async () => {
      try {
        if (factoryId) {
          return await shiftService.getShiftsByFactory(factoryId);
        }
        return await shiftService.getAllShifts();
      } catch (err) {
        console.error('Error cargando turnos:', err);
        throw err;
      }
    },
    retry: 1,
  });

  const safeTurnosData = turnosData ?? EMPTY_TURNOS;

  // Procesar y formatear turnos
  const turnos = useMemo(() => {
    const raw = Array.isArray(safeTurnosData) ? safeTurnosData : EMPTY_TURNOS;
    return raw.map((s) => ({
      ...s,
      nombre: s.name,
      hora_inicio: formatTimeForDisplay(s.start_time),
      hora_fin: formatTimeForDisplay(s.end_time),
    }));
  }, [safeTurnosData]);

  // Ordenar: primero activos, luego por start_time
  const turnosOrdenados = useMemo(() => {
    return [...turnos].sort((a, b) => {
      if (a.active !== b.active) return a.active ? -1 : 1;
      const sa = a.start_time || '';
      const sb = b.start_time || '';
      return sa.localeCompare(sb);
    });
  }, [turnos]);

  // Establecer turno seleccionado por defecto si no hay uno (debe ser useEffect, no useMemo)
  useEffect(() => {
    if (!turnoSeleccionado && turnosOrdenados.length > 0) {
      const activo = turnosOrdenados.find((t) => t.active) || turnosOrdenados[0];
      setTurnoSeleccionado(activo);
    }
  }, [turnosOrdenados, turnoSeleccionado]);

  const error = queryError?.message || null;

  return {
    turnos,
    turnosOrdenados,
    turnoSeleccionado,
    setTurnoSeleccionado,
    loading,
    error,
    cargarTurnos
  };
};
